import { ErrorMessage } from "@hookform/error-message";
import { yupResolver } from "@hookform/resolvers/yup";
import { AxiosResponse } from "axios";
import { IPatient, patients } from "lobbie";
import { useCallback, useEffect, useMemo, useState } from "react";
import { Button, Form } from "react-bootstrap";
import { useForm } from "react-hook-form";
import { useHistory } from "react-router";
import { VALID_PASSWORD_MESSAGE } from "src/constants";
import { useLobbiePatient } from "src/hooks/patients/useLobbiePatient";
import { useAxiosPutter } from "src/hooks/useAxios";
import { handleHttpError, notify } from "src/utils";
import { PATIENT_VALIDATION_SCHEMA } from "src/utils/validations/patient";
import * as yup from "yup";
import FlexRowDiv from "../shared/elements/FlexRowDiv";
import FormFieldError from "../shared/forms/FormFieldError";
import HelpTooltip from "../shared/tooltips/HelpTooltip";
import FullScreenLoadingSpinner from "../shared/loading/FullScreenLoadingSpinner";
import LobbieSpinner from "../shared/loading/LobbieSpinner";
import LobbieErrorMessage from "../shared/LobbieErrorMessage";
import PhoneFormController from "../shared/useForm/PhoneFormController";

const VALIDATION_SCHEMA = yup
    .object()
    .shape({
        receiveSms: yup.boolean(),
        receiveEmail: yup.boolean(),
        changePassword: yup.boolean(),
        currentPassword: yup.string().when("changePassword", {
            is: true,
            then: (schema) => schema.required("Current password is required"),
        }),
        newPassword: yup.string().when("changePassword", {
            is: true,
            then: (schema) =>
                schema
                    .required("New password is required")
                    .min(8, "New password must be 8 or more characters."),
        }),
        newPasswordConfirm: yup.string().when("changePassword", {
            is: true,
            then: (schema) =>
                schema.required().oneOf([yup.ref("newPassword")], "Passwords must match."),
        }),
    })
    .concat(PATIENT_VALIDATION_SCHEMA);

interface IProps {
    user: patients.IPatientAccountPatient | undefined;
    setUser: (newPatient: patients.IPatientAccountPatient) => void;
}

const PatientProfile: React.FC<IProps> = ({ user, setUser }) => {
    const { goBack } = useHistory();
    const putter = useAxiosPutter();
    const [patient] = useLobbiePatient();
    const [isSubmitting, setSubmitting] = useState<boolean>(false);

    const defaultValues = useMemo(
        () => ({
            firstName: user?.firstName ?? "",
            lastName: user?.lastName ?? "",
            email: user?.email ?? "",
            mobilePhone: user?.mobilePhone ?? "",
            receiveSms: user?.receiveSms ?? "",
            receiveEmail: user?.receiveEmail ?? "",
            changePassword: false,
            currentPassword: "",
            newPassword: "",
            newPasswordConfirm: "",
        }),
        [user],
    );

    const {
        register,
        control,
        formState: { errors },
        handleSubmit,
        watch,
        reset,
    } = useForm<any>({
        resolver: yupResolver(VALIDATION_SCHEMA),
        defaultValues,
    });

    const watches = {
        changePassword: watch("changePassword"),
        receiveSms: watch("receiveSms"),
        receiveEmail: watch("receiveEmail"),
    };

    useEffect(() => {
        reset(patient);
    }, [reset, patient]);

    const onSubmit = useCallback(
        async (_data: Record<string, any>) => {
            setSubmitting(true);

            // eslint-disable-next-line unused-imports/no-unused-vars
            const { dateOfBirth, ...data } = _data;

            await putter("/patient/update", data)
                .then((response: AxiosResponse | void) => {
                    if (!response) {
                        return;
                    }
                    const result = response.data as IPatient;
                    if (!result) {
                        response?.status &&
                            response.status > 399 &&
                            notify({
                                level: "error",
                                title: "Error",
                                message: "Error updating. Please try again.",
                            });
                        return;
                    }

                    setUser(result);
                    notify({
                        level: "success",
                        title: "Success",
                        message: "Account updated!",
                    });
                    setSubmitting(false);
                })
                .catch((error) => {
                    setSubmitting(false);
                    handleHttpError(error);
                });
        },
        [putter, setUser],
    );

    if (!user) {
        return <FullScreenLoadingSpinner />;
    }

    return (
        <div className="profile-card mb-5">
            <div className="mt-3">
                <h3>
                    Settings
                    <br />
                    &amp; Account information
                </h3>
            </div>
            <div>
                <Form onSubmit={handleSubmit(onSubmit)}>
                    <h4>Personal Information</h4>
                    <hr />
                    <Form.Group controlId="firstName">
                        <Form.Label>First Name</Form.Label>
                        <Form.Control
                            {...register("firstName")}
                            type="text"
                            isInvalid={!!errors.firstName?.message}
                            autoComplete="given-name"
                        />
                        <ErrorMessage as={LobbieErrorMessage} errors={errors} name={"firstName"} />
                    </Form.Group>
                    <Form.Group controlId="lastName">
                        <Form.Label>Last Name</Form.Label>
                        <Form.Control
                            {...register("lastName")}
                            type="text"
                            isInvalid={!!errors.lastName?.message}
                            autoComplete="family-name"
                        />
                        <ErrorMessage as={LobbieErrorMessage} errors={errors} name={"lastName"} />
                    </Form.Group>
                    <Form.Group controlId="email">
                        <Form.Label>Email address</Form.Label>
                        <Form.Control
                            {...register("email")}
                            isInvalid={!!errors.email?.message}
                            type="email"
                            autoComplete="email"
                        />
                        <ErrorMessage as={LobbieErrorMessage} errors={errors} name={"email"} />
                    </Form.Group>
                    <Form.Group controlId="mobilePhone" className="mb-5">
                        <Form.Label>Mobile Phone</Form.Label>
                        <PhoneFormController
                            control={control}
                            placeholder={"Enter mobile phone..."}
                            name="mobilePhone"
                            disabled={isSubmitting}
                        />
                        <ErrorMessage
                            as={LobbieErrorMessage}
                            errors={errors}
                            name={"mobilePhone"}
                        />
                    </Form.Group>
                    <h4>Notifications</h4>
                    <hr />
                    <div>
                        <label className="switch" htmlFor="receiveSms">
                            <input type="checkbox" {...register("receiveSms")} id="receiveSms" />
                            <span className="slider round"></span>
                        </label>
                        <span className="toggle-label gray2">
                            Receive text messages:&nbsp;
                            <span className="bold">{watches.receiveSms ? "On" : "Off"}</span>
                        </span>
                    </div>
                    <div className="mt-2 mb-5">
                        <label className="switch" htmlFor="receiveEmail">
                            <input
                                type="checkbox"
                                {...register("receiveEmail")}
                                id="receiveEmail"
                            />
                            <span className="slider round"></span>
                        </label>
                        <span className="toggle-label gray2">
                            Receive emails:&nbsp;
                            <span className="bold">{watches.receiveEmail ? "On" : "Off"}</span>
                        </span>
                    </div>
                    <h4>Password</h4>
                    <hr />
                    <div className="mt-2 mb-5">
                        <label className="switch" htmlFor="changePassword">
                            <input
                                type="checkbox"
                                id="changePassword"
                                {...register("changePassword")}
                            />
                            <span className="slider round"></span>
                        </label>
                        <span className="toggle-label gray2">Change account password</span>
                    </div>
                    {watches.changePassword && (
                        <>
                            <Form.Group controlId="currentPassword">
                                <Form.Label>Current password</Form.Label>
                                <Form.Control
                                    {...register("currentPassword")}
                                    isInvalid={!!errors.currentPassword?.message}
                                    type="password"
                                    autoComplete="current-password"
                                />
                                <FormFieldError message={errors.currentPassword?.message} />
                            </Form.Group>
                            <Form.Group controlId="newPassword">
                                <HelpTooltip
                                    message={VALID_PASSWORD_MESSAGE}
                                    tooltipId="passwordTooltip"
                                    title="New password"
                                />
                                <Form.Control
                                    {...register("newPassword")}
                                    isInvalid={!!errors.newPassword?.message}
                                    type="password"
                                    autoComplete="new-password"
                                />
                                <span className="body-small gray2">{VALID_PASSWORD_MESSAGE}</span>
                                <FormFieldError message={errors.newPassword?.message} />
                            </Form.Group>
                            <Form.Group controlId="newPasswordConfirm">
                                <Form.Label>Repeat new password</Form.Label>
                                <Form.Control
                                    {...register("newPasswordConfirm")}
                                    isInvalid={!!errors.newPasswordConfirm?.message}
                                    type="password"
                                    autoComplete="new-password"
                                />
                                <FormFieldError message={errors.newPasswordConfirm?.message} />
                            </Form.Group>
                        </>
                    )}
                    <FlexRowDiv justifyContent="space-between">
                        <Button
                            className="mt-4"
                            type="button"
                            variant="secondary"
                            disabled={isSubmitting}
                            onClick={goBack}
                        >
                            &nbsp;Cancel&nbsp;
                        </Button>
                        <Button
                            className="mt-4"
                            type="submit"
                            variant="primary"
                            disabled={isSubmitting}
                        >
                            &nbsp;Save account settings&nbsp;
                        </Button>
                    </FlexRowDiv>
                </Form>
                <div className="text-center mt-3 mb-3">
                    <LobbieSpinner isHidden={!isSubmitting} />
                </div>
            </div>
        </div>
    );
};

export default PatientProfile;
